import { useMemo, useRef, useState, useEffect } from 'react';
import { LogIn, Pencil, Truck, Banknote } from 'lucide-react';
import { useStartCountdown } from './hooks/useStartCountdown';
import StalyPruh from './StalyPruh';
import ShiftCardCta from './ShiftCardCta';
import DetailSmeny from './DetailSmeny';

const fmtTime = t => (t || '').toString().slice(0, 5);

const PHASE_COLOR = '#FF8A00';   // před zahájením — oranžová jako zahájení (modrá plocha tlačítka podle normy ne)

/**
 * Karta nadcházející směny pro stav `pre` — jediné místo, kde se směna
 * ukazuje **v plné šíři**: pruh s názvem a dlaždicemi, pod ním rozšířený
 * detail (kolik kontrol čeká, s kým je člověk na place, kdy končí) a ve
 * spodní hraně dělené CTA (Začít v X / Jiný čas).
 *
 * Je to jediná chvíle, kdy je na čtení čas. Po klepnutí na CTA se detail
 * odanimuje nahoru a pruh zůstane stát, takže přechod do přípravy čte jako
 * složení karty do hlavičky. Od té chvíle patří prostor práci — proto
 * v dalších fázích mizí i název směny.
 */
export default function ShiftCardPre({
    smena, tym = [],
    onClockIn, onClockInManual, onOpenTeam, onZpravaKolegovi,
    faze = [],
    clockBusy, isDark, isMobile = false,
}) {
    const { label: countdown, stav } = useStartCountdown(smena);

    // Odanimování detailu při zahájení. Pruh nahoře zůstává na místě, takže
    // se karta „složí do hlavičky" a teprve pak naskočí další krok — bez
    // toho to vypadalo jako skok na jinou obrazovku.
    const [zaviram, setZaviram] = useState(false);
    const casovac = useRef(null);
    useEffect(() => () => clearTimeout(casovac.current), []);

    const zahaj = (akce) => {
        if (zaviram) return;
        setZaviram(true);
        // Akce jde **hned**, ne až po animaci: složený pruh vypadá přesně jako
        // ten, co naskočí za ním (název se zasouvá s detailem), takže na
        // okamžiku výměny nezáleží. Čekat by znamenalo přidat půl vteřiny
        // mrtvého času k requestu, který stejně nějakou dobu trvá.
        akce?.();
        // Pojistka: kdyby zahájení selhalo, ať tu nezůstane složená a
        // nereagující karta.
        casovac.current = setTimeout(() => setZaviram(false), 4000);
    };

    // Tlačítko vždy ukazuje plánovaný cas_od — backend bere stejnou hodnotu jako
    // default clock_in. Manuální override jde přes dělené tlačítko „Jiný čas".
    const buttonTime = useMemo(() => fmtTime(smena?.cas_od), [smena?.cas_od]);

    // Zahájit jde jen směnu s dnešním datem — jinak je CTA disabled, ať nikdo
    // omylem nespustí směnu naplánovanou na jiný den.
    const jeDnes = useMemo(() => {
        if (!smena?.datum) return false;
        const d = new Date();
        const dnes = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        return String(smena.datum).slice(0, 10) === dnes;
    }, [smena?.datum]);

    if (!smena) return null;

    // Kdy směna začíná — vpravo vedle štítku s názvem. Odpočet tu stál
    // uprostřed osy, osamocený mezi Start a Konec; vedle názvu tvoří jednu
    // větu („Rozvoz večerní · začíná za 2h 3m") a osa nese jen časy.
    // Barva podle toho, jak moc to hoří: běžně bílá, posledních 30 minut
    // oranžová, po začátku červená.
    const stavMeta = {
        future:   { label: 'začíná za', color: isDark ? '#fff' : 'var(--text-primary)' },
        imminent: { label: 'začíná za', color: '#FF8A00' },
        started:  { label: 'zpoždění',  color: '#EF4444' },
    };
    const currentStav = stavMeta[stav] || stavMeta.future;
    const zacatek = countdown ? `${currentStav.label} ${countdown}` : null;

    const textMuted = isDark ? 'rgba(255,255,255,0.40)' : 'var(--text-muted)';

    // Datum a rozsah pod názvem se nekreslí — den je dnešek a časy nese osa
    // fází i tlačítko („Začít v 11:00"), takže pod názvem stály potřetí.
    //
    // Zůstává jediná věc, která se jinde nedozví: sloučené směny (dvě
    // naplánovaná okna stejné kariéry v jednom dni) jedou jako jedna. Bez
    // téhle poznámky zaměstnance překvapí, že „Začít v 11:00" platí až do
    // konce té večerní — osa ukáže jen výsledný konec, ne že vznikl ze dvou.
    const subtitle = smena.slouceni
        ? `${smena.slouceni.pocet} směny: ${smena.slouceni.casti.map(c => `${c.cas_od}–${c.cas_do}`).join(' + ')}`
        : null;

    const sloty = [
        {
            id: 'auto',
            variant: 'info',
            disabled: !smena.pozaduje_vozidlo,
            icon: <Truck size={14} />,
            label: 'Auto',
            value: smena.pozaduje_vozidlo ? 'Ano' : null,
            accent: '#3B82F6',
        },
        {
            id: 'pokladna',
            variant: 'info',
            disabled: !smena.pozaduje_pokladnu,
            icon: <Banknote size={14} />,
            label: 'Pokladna',
            value: smena.pozaduje_pokladnu ? 'Ano' : null,
            accent: '#10B981',
        },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minHeight: 0 }}>
            {/* Název nese karta ve všech fázích — je to odpověď na „kterou
                dnes dělám", a ta platí i uprostřed směny. */}
            <StalyPruh
                barvaFaze={PHASE_COLOR}
                nadpis={smena.sablon_nazev || 'Směna'}
                nadpisDoplnek={zacatek}
                nadpisDoplnekBarva={currentStav.color}
                podnadpis={subtitle}
                faze={faze}
                aktivniFazeId={null}
                sloty={sloty}
                tym={tym}
                onTym={onOpenTeam}
                sbaleno={zaviram}
                isDark={isDark}
                isMobile={isMobile}
                /* Tlačítko je spodní hranou karty — pod detailem, takže se
                   s ním při zahájení zasune. Před zahájením je obrazovka
                   skoro prázdná — osamocené tlačítko dole by se hledalo,
                   takhle ho oko najde na kartě samé. */
                akce={
                    <ShiftCardCta
                        label={`Začít v ${buttonTime}`}
                        icon={<LogIn size={18} />}
                        onClick={() => zahaj(onClockIn)}
                        busy={clockBusy || zaviram}
                        busyLabel="Zahajuji…"
                        barva={PHASE_COLOR}
                        /* Disabled, pokud směna není dnes — ochrana proti omylu. */
                        disabled={!jeDnes}
                        secondary={{
                            icon: <Pencil size={13} />,
                            label: 'Jiný čas',
                            onClick: onClockInManual,
                            title: 'Začít s jiným časem',
                        }}
                        isDark={isDark}
                    />
                }
            >
                <DetailSmeny faze={faze} tym={tym}
                    sbaleno={zaviram} onZprava={onZpravaKolegovi} isDark={isDark} />
            </StalyPruh>
        </div>
    );
}

